import { AppButton, AppTable, AppTag, AppTooltip } from '../atoms';
import type { AppTableColumn } from '../atoms/AppTable';
import { Download } from '../atoms/AppIcon';
import { getServiceTypeLabel } from '@/constants/serviceType';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import { convGtJDate, convGtJDateTime } from '@/utils/dateConverter';
import {
  INBOUND_KIND_COLOR,
  LEVEL_COLUMN_TITLE,
  LEVEL_LABEL,
  INBOUND_KIND_LABEL,
  MASTER_CHANGE,
  MASTER_KIND_COLOR,
  NOTIFICATION_OPERATION,
  NOTIFICATION_OPERATION_COLOR,
  NOTIFICATION_OPERATION_LABEL,
  MASTER_KIND_LABEL,
  MASTER_OUTCOME,
  MASTER_SUBJECT,
  NOTIFICATION_TAB,
  PATIENT_COLUMN,
  type NotificationItem,
  type NotificationRecord,
  type NotificationSend,
  type NotificationTab,
} from '@/types/admin-notification-center';

interface NotificationCenterTableProps {
  tab: NotificationTab;
  /**
   * Draw 治療院ID / 治療院名. TRUE for the admin portal, FALSE for the 治療院 one.
   *
   * 👤 owner, 2026-09-02: 「bỏ cột tên cơ sở và clinic_number đi vì đã đang login trong clinic đó rồi」.
   * Inside one clinic both columns repeat the same value on every row, which costs the width that 内容 —
   * the column anyone actually reads — needs.
   *
   * A PROP RATHER THAN A SECOND TABLE. The two screens must not drift: everything else here (the derived
   * 種別, the stacked 内容, the named per-rezept lines, the エラー詳細 gate) is logic the clinic copy has to
   * inherit, and a fork would inherit only today's version of it.
   */
  showClinicColumns?: boolean;
  /**
   * End a re-sent-and-failed-again row with 「管理者にご連絡ください」.
   *
   * Owner, 2026-09-03: 「RAH admin thằng này k cần thông báo liên hệ admin vì nó là admin rồi. RAH clinic
   * thì thêm thông báo ở cuối như thằng shinsa」. Telling the administrator to contact the administrator is
   * noise on the one screen where the reader is already the person who would be contacted.
   *
   * ITS OWN FLAG, not a re-use of showClinicColumns: that one answers 「is the reader inside a single
   * clinic」 for column width, and pointing at it here would tie two unrelated decisions together — and
   * invert, since the line is wanted exactly where those columns are not.
   */
  showAdminContactHint?: boolean;
  data: NotificationItem[];
  isLoading?: boolean;
  hasFilters?: boolean;
  /** Ids ticked right now. Owned by the template so the bulk bar and the table cannot disagree. */
  selectedIds: number[];
  onSelectionChange: (ids: number[]) => void;
  /** エラー詳細 — hands the operator a CSV of everything not shown in the row. */
  onDownloadError: (item: NotificationItem) => void;
  /**
   * 不一致明細 — the same column, a DIFFERENT file. A 状態の不一致 row has no failed sends to describe; what it
   * has is the list of rezepts the two systems disagree about, written to disk by the nightly reconcile.
   */
  onDownloadDrift?: (item: NotificationItem) => void;
  disabled?: boolean;
}

/* ---------------------------------------------------------------------------------------- cells */

/**
 * 種別 — the column every row is scanned for. 情報 / 成功 / エラー.
 *
 * A WORD plus a colour plus a shape, never colour alone: about one man in twenty cannot separate this red
 * from this green, and the whole screen turns on the distinction. The row's left bar and tint (see
 * .app-noti-table in the stylesheet) repeat it a third time for someone skimming at arm's length.
 *
 * 情報 is deliberately BLUE, not amber: amber on this palette sits next to the 返戻取消 gold used one tab
 * over, and a run that has just started is not a warning — it is 「come back later」. It also SPINS, which
 * is the one signal a static tag cannot give: this row will change on its own.
 *
 * THE SPINNER FOLLOWS THE 種別, NOT `finishedAt`. On the two sync tabs 情報 means exactly 「records are
 * still in flight」 — that is how the level is computed, from the states rather than from a timestamp — so
 * the tag being 情報 is already the whole condition.
 *
 * It used to read `finishedAt === null`, and that broke the moment re-sends folded into the row: a
 * re-send runs LONG AFTER the press itself finished, so the row's own finished_at is set while the row is
 * still 情報, and the spinner vanished exactly when it was most needed. Do not reintroduce a timestamp
 * here — a row can be open without its first send being open.
 *
 * 審査システムからの通知 is the exception and keeps its tab check: every row there is 情報 and none of them
 * is running, so a spinner would never stop.
 */
const LevelTag = ({ item }: { item: NotificationItem }) => {
  if (item.level === 'info') {
    return (
      <AppTag color="processing" className="!m-0 !px-2 whitespace-nowrap">
        {item.tab !== NOTIFICATION_TAB.INBOUND && (
          <i className="fa fa-refresh fa-spin mr-1" aria-hidden="true" />
        )}
        {LEVEL_LABEL.info}
      </AppTag>
    );
  }

  return item.level === 'error' ? (
    <AppTag color="error" className="!m-0 !px-2 font-semibold whitespace-nowrap">
      <i className="fa fa-exclamation-circle mr-1" aria-hidden="true" />
      {LEVEL_LABEL.error}
    </AppTag>
  ) : (
    <AppTag color="success" className="!m-0 !px-2 whitespace-nowrap">
      <i className="fa fa-check-circle mr-1" aria-hidden="true" />
      {LEVEL_LABEL.success}
    </AppTag>
  );
};

/**
 * 和暦 date + seconds. `convGtJDateTime` stops at minutes, and a 1-second run has to stay legible next to a
 * 28-second one, so the seconds are appended to `convGtJDate` here.
 */
const wareki = (value: Dayjs): string => `${convGtJDate(value)} ${value.format('HH:mm:ss')}`;

/**
 * 受信日時 — one moment, in the house 和暦 form, same as every other date on this screen.
 *
 * The compact 「R08.08.27」 form was used here for a while to save table width; it is not needed on the
 * inbound tab, which carries six columns and hands 内容 more than half the table anyway.
 */
const Stamp = ({ value }: { value: string | null }) => {
  if (!value) return <span className="text-gray-300">—</span>;

  return <span className="whitespace-nowrap">{convGtJDateTime(value)}</span>;
};

/** 「202607」 is a key, 「2026年07月」 is a month. The column is read by people, so it shows the month. */
const yearMonth = (value: string | null): string =>
  value && value.length === 6 ? `${value.slice(0, 4)}年${value.slice(4)}月` : (value ?? '-');

/**
 * 内容 — 👤 owner's manager, 2026-08-27. Three shapes, one per 種別, and the row COMPOSES them rather than
 * storing a sentence:
 *
 *   情報   同期を開始しました
 *          同期時間：令和08年08月27日 03:17:01          <- one moment, no arrow: the run has not ended
 *          対象 40件
 *   成功   同期が完了しました
 *          同期時間：令和08年08月27日 05:24:30 〜 05:35:00
 *          対象 120件
 *   エラー  同期でエラーが発生しました
 *          同期時間：令和08年08月27日 05:24:30 〜 05:35:00
 *          120件中 118件成功、2件エラー
 *          <what actually failed>
 *
 * ALL THREE ARE THE SAME SHAPE — 👤 owner, 2026-08-28: verb, 同期時間, then the counts. 情報 used to break
 * that pattern twice, folding its count into the first line and printing no time at all, which left the one
 * row a reader opens the screen to check with the coarsest timestamp on it.
 *
 * COMPOSED, NOT STORED, and that is deliberate. The three lines are the same facts the row already carries
 * (level, window, counts), so writing them into a string on the server would mean a second copy free to
 * disagree with the columns beside it — and it would have to be translated per locale. What the server
 * still owns is the LAST line: which failure it was, which no formula can produce.
 *
 * THE ARROW, not the line, is what gets dropped while the run is open — see SyncWindow.
 */
const SYNC_LINE: Record<'info' | 'success' | 'error', string> = {
  info: '同期を開始しました',
  success: '同期が完了しました',
  error: '同期でエラーが発生しました',
};

const count = (n: number | null): string => (n === null ? '' : n.toLocaleString('ja-JP'));

/**
 * The line a row earns by being re-sent BY HAND and failing again.
 *
 * Owner, 2026-09-03: 「nếu bấm retry mà vẫn lỗi thì thêm 1 message ở dưới là thử lại thất bại. vui lòng
 * liên hệ admin (cái này apply cho cả RAH clinic và Shinsa)」. Worded identically on both systems
 * (Shinsa AppConstants::SYNC_NOTIFICATION_RETRY_FAILED_NOTICE) — one incident, one sentence, whichever
 * screen the reader is on.
 *
 * ONLY AFTER A PRESS. The pipeline retries on its own for half an hour, and 「contact your administrator」
 * on a row the queue is still working reads as a dead end when it is not. Both portals get it because both
 * render this table.
 */
const RETRY_FAILED_NOTICE = '再送信しましたが、同じエラーが発生しました。管理者にご連絡ください。';

/**
 * Has a person pressed 再送信 on this row and been refused again?
 *
 * READ FROM THE LAST ATTEMPT, not from the row totals: `failureCount` is the press's own tally and stays
 * non-zero after a re-send has since repaired every record. The last block in `sends` IS the re-send, so
 * its own verdict is the one that answers the question.
 *
 * 処理中 SUPPRESSES IT. A re-send still draining the queue has no verdict yet (SendTally makes the same
 * distinction, and for the same reason: a number that changes its mind teaches people to distrust it).
 */
const retryFailedAgain = (item: NotificationItem): boolean => {
  if (item.retryCount <= 0) return false;

  const last = item.sends.at(-1);

  // No `sends` — a マスタ同期 row, or one pressed seconds ago whose audit trail has not been written yet.
  // Fall back to the row's own counts, which is all such a row has.
  if (!last) return (item.failureCount ?? 0) > 0;

  return last.running === 0 && last.failure > 0;
};

const RetryFailedNotice = ({ item, show }: { item: NotificationItem; show: boolean }) =>
  show && retryFailedAgain(item) ? (
    <span className="block text-xs font-semibold text-red-600">{RETRY_FAILED_NOTICE}</span>
  ) : null;

const SyncWindow = ({
  startedAt,
  finishedAt,
  label = '同期時間',
}: {
  startedAt: string | null;
  finishedAt: string | null;
  /** 再送信時間 on a re-send block, so two blocks with two clocks cannot be read as one long sync. */
  label?: string;
}) => {
  if (!startedAt) return null;

  const start = dayjs(startedAt);

  /**
   * STILL RUNNING — one moment, and NO ARROW. 👤 Owner, 2026-08-28.
   *
   * This line used to be dropped entirely while `finishedAt` was null, to avoid printing 「05:24:30 〜 」 with
   * an empty right-hand side, which reads as a broken cell rather than as 「still going」. The objection was
   * right about the arrow and wrong about the line: dropping it left the running row with no time finer than
   * the 日時 column's minute, and at 500 rezepts in 5.6 seconds several runs share a minute.
   *
   * A lone start moment is neither problem. It is also what the feedback asked for.
   */
  if (!finishedAt) {
    return (
      <span className="block text-xs whitespace-nowrap text-gray-500 tabular-nums">
        {label}：{wareki(start)}
      </span>
    );
  }

  const end = dayjs(finishedAt);
  // The end repeats only the CLOCK on a same-day send — the date is already on the same line. A send that
  // crosses midnight prints both dates in full, because then the date is load-bearing.
  const sameDay = start.format('YYYY-MM-DD') === end.format('YYYY-MM-DD');

  return (
    <span className="block text-xs whitespace-nowrap text-gray-500 tabular-nums">
      {label}：{wareki(start)} 〜 {sameDay ? end.format('HH:mm:ss') : wareki(end)}
    </span>
  );
};

/**
 * 内容 on マスタ同期 — ONE SENTENCE, replacing the count line entirely. 👤 Owner, 2026-09-02:
 * 「ở tab data master tôi k muốn ... 1件中 0件成功、1件エラー ... vì cơ sở và bảo hiểm đồng bộ thì chỉ 1
 * record thôi」.
 *
 * The count was worse than useless here. A 治療院 / 保険者 send is ONE record, so 「1件中」 tells the reader
 * nothing — and the two halves of the cell were drawn from different sources: the tag from the record's
 * live state, the count from that ONE send's audit rows. A record whose second dispatch was coalesced away
 * has no audit row for it, so the row showed 「成功」 above 「1件エラー」 (screenshot, 2026-09-02).
 *
 */
const masterSentence = (item: NotificationItem): string | null => {
  // GATED ON THE TAB, not on masterKind alone. Only マスタ同期 rows get this sentence; a row on another tab
  // that happens to carry a kind must keep the count its own tab is built around.
  if (item.tab !== NOTIFICATION_TAB.MASTER || item.masterKind === null) return null;

  const subject = MASTER_SUBJECT[item.masterKind];
  // A master whose row predates the flag (isNew === null) drops the 新規登録 / 更新 half rather than
  // guessing which it was.
  const change = item.isNew === null ? '' : MASTER_CHANGE[item.isNew ? 'new' : 'update'];

  return `${subject}${change}${MASTER_OUTCOME[item.level]}`;
};

/**
 * 内容 for 確定取消 and 返戻取消依頼 — ONE LINE PER REZEPT, naming the patient.
 *
 * 👤 Owner, 2026-09-02: 「với 2 loại này thì do đi theo từng rezepts nên note rõ là của bệnh nhân nào, id nào
 * … thay cho dòng này: 1件中 1件成功、0件エラー」.
 *
 * 「1件中 1件成功、0件エラー」 is the right shape for a 確定 press, whose subject is a whole clinic-month and
 * where no single rezept is the point. On these two it says nothing a reader can act on: they are decisions
 * about ONE PATIENT'S rezept, and 「1件成功」 still leaves them to go and find out whose.
 *
 * THE OUTCOME WORD IS THE SAME VOCABULARY as the 種別 tag (LEVEL_LABEL), so a line and the tag above it can
 * never read as two different verdicts on the same thing.
 *
 * The cap is the SERVER'S, and what it left out is stated rather than dropped — 返戻取消 runs over an
 * unbounded selection, so a press can cover hundreds, and a list that silently stops reads as complete.
 */
/** The two operations whose 内容 names rezepts instead of counting them. */
const isNamedOperation = (item: NotificationItem): boolean =>
  item.operation !== null && item.operation !== NOTIFICATION_OPERATION.UPSERT;

const OUTCOME_LABEL: Record<string, string> = {
  success: LEVEL_LABEL.success,
  failure: LEVEL_LABEL.error,
  running: '処理中',
};

const OUTCOME_CLASS: Record<string, string> = {
  success: '',
  failure: 'font-semibold text-red-600',
  running: '',
};

/** 「R2-446651」 — the id an operator can paste into a search box, not the internal aggregate name. */
const rezeptLabel = (record: NotificationRecord): string =>
  `${record.aggregate.replace('rezept-', '').toUpperCase()}-${record.aggregateId}`;

const NamedRecords = ({ item }: { item: NotificationItem }) => {
  if (item.records.length === 0) return null;

  const hidden = Math.max(0, item.recordsTotal - item.records.length);

  return (
    <div className="pt-1">
      {item.records.map(record => (
        <span key={`${record.aggregate}:${record.aggregateId}`} className="block">
          {record.patientName ?? '受診者不明'}
          {record.patientSerial !== null && `（${PATIENT_COLUMN.SERIAL} ${record.patientSerial}）`}
          {' ／ '}
          {rezeptLabel(record)}：
          <span className={OUTCOME_CLASS[record.outcome] ?? ''}>
            {OUTCOME_LABEL[record.outcome] ?? record.outcome}
          </span>
        </span>
      ))}
      {hidden > 0 && <span className="block text-gray-500">ほか {hidden.toLocaleString('ja-JP')}件</span>}
    </div>
  );
};

/**
 * 「500件中 400件成功、100件処理中」 — the three buckets of ONE send.
 *
 * 処理中 IS SHOWN SEPARATELY FROM エラー, and that distinction is the point. A rezept whose push failed on a
 * timeout is retried up to four times over twenty minutes (SyncPushJob::backoff), and the failed attempt has
 * ALREADY written its error row. Folding those into エラー puts 「100件エラー」 on screen for a hundred rezepts
 * the queue is still working through, and then quietly changes its mind ten minutes later — which teaches
 * people to distrust the number. Owner, 2026-09-01: 「nếu đang retry thì vẫn báo đang xử lý ấy」.
 *
 * Each bucket is dropped when it is zero, EXCEPT 成功 — 「0件成功」 on a settled send is information, not
 * noise, and a finished block that showed only errors would read as an incomplete sentence.
 */
const SendTally = ({ send }: { send: NotificationSend }) => (
  <span className="block text-xs whitespace-nowrap text-gray-500 tabular-nums">
    {count(send.total)}件中 {count(send.success)}件成功
    {send.failure > 0 && (
      <>
        、<span className="font-semibold text-red-600">{count(send.failure)}件エラー</span>
      </>
    )}
    {/* Nothing has gone wrong yet and nothing may: while records are still in flight the send has no
        verdict, so the block reports what is outstanding rather than guessing at one. */}
    {send.running > 0 && <>、{count(send.running)}件処理中</>}
    {send.failure === 0 && send.running === 0 && <>、0件エラー</>}
    {/* 対象外 — OUTSIDE the three buckets and outside 件中, so the sentence still adds up: a rezept whose
        保険者 is on 償還払い was never sent and was never meant to be. Bracketed rather than joined with 、
        because it does not describe an outcome of the send; it says a record left the send. */}
    {send.outOfScope > 0 && <>（対象外 {count(send.outOfScope)}件）</>}
  </span>
);

const Summary = ({ item, showAdminContactHint }: { item: NotificationItem; showAdminContactHint: boolean }) => {
  // 審査システムからの通知 — one レセプト for one person. No send, no window, no counts: the sentence Shinsa
  // sent IS the content, and wrapping it in 「同期が完了しました」 would describe something that did not
  // happen here.
  //
  // A STORED SENTENCE ALSO ARRIVES ON レセプト同期 since 2026-09-03: 状態の不一致, the nightly reconcile's
  // finding. It is not a press either — there are no counts to compose from, and composing anyway would
  // print 「同期が完了しました ／ 対象 0件」 over a row that says the two systems disagree.
  //
  // TESTED ON THE DATA, not on the category: the server sends `body` only where a row owns its sentence
  // (the two press tabs store none), so any future notice that carries one renders correctly without a
  // second condition here.
  if (item.tab === NOTIFICATION_TAB.INBOUND || item.summary !== '') {
    return (
      <AppTooltip title={item.summary} showOnlyOnOverflow>
        <span className="app-noti-summary line-clamp-2 min-w-[220px] whitespace-normal break-words">
          {item.summary}
        </span>
      </AppTooltip>
    );
  }

  /**
   * NO `sends` — the マスタ同期 tab, and any row written before the stack existed. One block, and the
   * ORIGINAL rule: 対象 N件 while it is going or once it is clean, the success/failure split only when
   * something failed. Left exactly as it was so that tab does not change under a rezept-tab decision.
   */
  if (item.sends.length === 0) {
    const total = count(item.totalCount);
    // A row is 「sends: []」 in two situations that must not render the same way: a マスタ同期 row, which has
    // no press at all, and a 確定取消 / 返戻取消依頼 that was pressed SECONDS AGO — its audit trail is what
    // builds `sends`, and it is written when the job runs, not when the button is clicked. Falling through
    // to 対象 N件 there would show a count on exactly the two operations the count was removed from, in the
    // window where somebody is most likely to be looking.
    const named = isNamedOperation(item);
    const master = masterSentence(item);

    return (
      <div className="app-noti-summary min-w-[260px] space-y-0.5 whitespace-normal break-words">
        <span className="block font-medium">{master ?? SYNC_LINE[item.level]}</span>

        <SyncWindow startedAt={item.startedAt} finishedAt={item.finishedAt} />

        {master === null && !named && (item.level === 'info' || item.level === 'success') && total !== '' && (
          <span className="block text-xs text-gray-500 tabular-nums">
            対象 {total}件
            {/* A press whose only non-success was 対象外 now reads 成功 and lands here, so the excluded
                records have to be named on this branch too — otherwise they vanish from the screen. */}
            {(item.outOfScopeCount ?? 0) > 0 && <>（対象外 {count(item.outOfScopeCount)}件）</>}
          </span>
        )}

        {master === null && !named && item.level === 'error' && total !== '' && (
          <span className="block text-xs whitespace-nowrap text-gray-500 tabular-nums">
            {total}件中 {count(item.successCount)}件成功、
            <span className="font-semibold text-red-600">{count(item.failureCount)}件エラー</span>
            {(item.outOfScopeCount ?? 0) > 0 && <>（対象外 {count(item.outOfScopeCount)}件）</>}
          </span>
        )}

        {named && <NamedRecords item={item} />}

        <RetryFailedNotice item={item} show={showAdminContactHint} />
      </div>
    );
  }

  /**
   * ONE BLOCK PER SEND, oldest first — owner, 2026-09-01: 「mỗi lần thử lại cứ thêm vào xuống dưới thôi」.
   *
   * The verb at the top says where the press STANDS NOW; each block below says what one attempt DID, with
   * its own clock (「同期時間 => đổi thành thời gian thử lại」). So a press that failed one rezept and was then
   * re-sent reads:
   *
   *   同期が完了しました
   *   同期時間：令和08年09月01日 02:42:50 〜 02:42:50
   *   4件中 3件成功、1件エラー
   *   同期時間：令和08年09月01日 10:22:52 〜 10:22:52
   *   1件中 1件成功、0件エラー
   *
   * A SETTLED BLOCK ALWAYS PRINTS THE SPLIT, including 「0件エラー」. That zero is the point of the stack: it
   * is what tells a reader the re-send finished the job, and dropping it would leave the last block looking
   * like an unexplained extra line.
   */
  return (
    <div className="app-noti-summary min-w-[260px] space-y-0.5 whitespace-normal break-words">
      <span className="block font-medium">{masterSentence(item) ?? SYNC_LINE[item.level]}</span>

      {item.sends.map((send, index) => (
        <div key={send.correlationId || index} className={index > 0 ? 'pt-1' : undefined}>
          <SyncWindow
            startedAt={send.startedAt}
            finishedAt={send.finishedAt}
            label={index === 0 ? '同期時間' : '再送信時間'}
          />
          {/* The count belongs to ONE ATTEMPT; the named list belongs to the ROW, because an outcome is read
              live from the record's current state and is not a property of the attempt that last touched it.
              So the tally stays inside the per-send block and the names sit once, below all of them. */}
          {/* No count on マスタ同期: the row IS one record and the sentence above already said what
              happened to it. */}
          {!isNamedOperation(item) && masterSentence(item) === null && <SendTally send={send} />}
        </div>
      ))}

      {isNamedOperation(item) && <NamedRecords item={item} />}

      {/* Below every block, because it is about the row as it stands now — not about one attempt. */}
      <RetryFailedNotice item={item} show={showAdminContactHint} />
    </div>
  );
};

/**
 * Is there anything to download for this row?
 *
 * ON レセプト同期 THE ANSWER IS 「did any send fail」, not 「is there an errorDetail object」. That object is a
 * concept-screen shape the real API never sends for a press: the file is built server-side from
 * sync_audit_logs, one line per failed rezept per attempt, and it covers the re-sends too. Gating on the
 * old field left the button greyed out on exactly the rows that had a file waiting.
 *
 * IT FOLLOWS THE FILE, NOT THE HISTORY (fixed 2026-09-02). This used to also return true when any past
 * send had failed, on the argument that a repaired press 「still has a file worth reading」. It does not:
 * the server builds the file from the LATEST VERSION of each record only (owner, 2026-09-01: 「chỉ lấy lỗi
 * cuối cùng tức là sync_audit log của version cuối cùng thôi」), so a record repaired by a re-send drops
 * out — and the button was offering a download that came back with nothing but headers.
 *
 * Measured on ra2_db 2026-09-02: a 返戻取消依頼 that failed once and was repaired showed sends [1, 0]
 * failures, the button, and a file of ZERO rows. A button that yields an empty file is worse than no
 * button: it reads as 「the detail is missing」 rather than 「there is nothing wrong any more」.
 */
const hasErrorDetail = (item: NotificationItem): boolean =>
  item.errorDetail !== null || (item.failureCount ?? 0) > 0;

/* --------------------------------------------------------------------------------------- table */

const NotificationCenterTable = ({
  tab,
  showClinicColumns = true,
  showAdminContactHint = false,
  data,
  isLoading,
  hasFilters,
  selectedIds,
  onSelectionChange,
  onDownloadError,
  onDownloadDrift,
  disabled,
}: NotificationCenterTableProps) => {
  const isMaster = tab === NOTIFICATION_TAB.MASTER;
  const isInbound = tab === NOTIFICATION_TAB.INBOUND;
  const isRezept = tab === NOTIFICATION_TAB.REZEPT;

  const columns: AppTableColumn<NotificationItem>[] = [
    // 種別 — first column on every tab, and the tab decides its vocabulary.
    //
    //   sync tabs  情報 / 成功 / エラー   — what happened to the run
    //   inbound    返戻 / 返戻取消 / …    — what 審査 sent back
    //
    // ONE column, not two, even though inbound rows also carry a level: everything on that tab is 情報
    // (nothing there can fail), so a second column would repeat one word down every row.
    ...(isInbound
      ? [
          {
            title: '種別',
            key: 'inboundKind',
            render: (_: unknown, record: NotificationItem) =>
              record.inboundKind ? (
                <AppTag
                  color={INBOUND_KIND_COLOR[record.inboundKind]}
                  className="!m-0 font-semibold whitespace-nowrap"
                >
                  {INBOUND_KIND_LABEL[record.inboundKind]}
                </AppTag>
              ) : (
                <span>-</span>
              ),
            width: 1,
            align: 'center' as const,
          },
        ]
      : [
          {
            title: LEVEL_COLUMN_TITLE,
            key: 'level',
            render: (_: unknown, record: NotificationItem) => <LevelTag item={record} />,
            width: 1,
            align: 'center' as const,
          },
        ]),

    // 日時 — ONE MOMENT: when this notification was raised. 👤 Owner's manager, 2026-08-27.
    //
    // It used to render the run's whole span (「開始 〜 終了」), which put two different questions in one
    // cell. The span is the RUN's property and now sits inside 内容 as 同期時間; this column answers
    // 「when did this land in my inbox」 — what the list is sorted by, and what pairs with 既読.
    {
      title: '日時',
      dataIndex: 'occurredAt',
      key: 'occurredAt',
      render: (value: string) => <Stamp value={value} />,
      width: 1,
    },

    // データ種別 — マスタ同期 only. WHICH master this run is about: 治療院 / 保険者.
    //
    // THE TAG ALONE — 👤 owner, 2026-08-27. It used to print the business code beside the tag, which was
    // the same value the next column now shows for every kind; two copies of one code down one row is
    // noise, and it made this column the widest thing on the tab for no gain.
    ...(isMaster
      ? [
          {
            title: 'データ種別',
            key: 'masterKind',
            render: (_: unknown, record: NotificationItem) =>
              record.masterKind ? (
                <AppTag color={MASTER_KIND_COLOR[record.masterKind]} className="!m-0 whitespace-nowrap">
                  {MASTER_KIND_LABEL[record.masterKind]}
                </AppTag>
              ) : (
                <span />
              ),
            width: 1,
            align: 'center' as const,
          },
        ]
      : []),

    /*
     * 操作 — レセプト同期 only, added 2026-09-02.
     *
     * WHY THE TAB NEEDED IT. This tab used to hold one kind of row (a 確定 press), so 「what happened」 was
     * answered by the tab name. It now holds three — 確定 / 確定取消 / 返戻取消依頼 — and without this column
     * a 返戻取消依頼 and an ordinary send are two identical-looking rows for the same clinic and 提出年月,
     * which is exactly the pair an operator must not confuse: one asked Shinsa a question, the other sent
     * money figures.
     *
     * THE WORDS ARE SHINSA'S. 確定取消 / 返戻取消依頼 are the names of the two tabs Shinsa files the same
     * events under (BD 7.2 / 7.3), so someone holding both screens open reads one vocabulary.
     *
     * A TAG, NOT PLAIN TEXT, and colour is never the only signal — the word is always there. 確定取消 and
     * 返戻取消依頼 are tinted apart from the ordinary send because they are the rare ones: on a list that is
     * overwhelmingly 確定 presses, the two exceptions have to catch the eye without being read first.
     */
    ...(isRezept
      ? [
          {
            title: '操作',
            key: 'operation',
            render: (_: unknown, record: NotificationItem) =>
              record.operation ? (
                <AppTag
                  color={NOTIFICATION_OPERATION_COLOR[record.operation]}
                  className="!m-0 whitespace-nowrap"
                >
                  {record.operationLabel ?? NOTIFICATION_OPERATION_LABEL[record.operation]}
                </AppTag>
              ) : (
                <span>-</span>
              ),
            width: 1,
            align: 'center' as const,
          },
        ]
      : []),

    // サービス区分 — レセプト同期 only. One run is one 確定 press for one service.
    ...(isRezept
      ? [
          {
            title: 'サービス区分',
            dataIndex: 'serviceType',
            key: 'serviceType',
            render: (value: 'A2' | 'R2' | null) =>
              value ? (
                <span className="whitespace-nowrap">{getServiceTypeLabel(value)}</span>
              ) : (
                <span>-</span>
              ),
            width: 1,
          },
        ]
      : []),

    // A CODE AND A NAME, on every tab — but they mean different things per tab, so they are NAMED
    // differently. 👤 Owner's manager 2026-08-27 asked for the pair; 👤 owner 2026-08-27 asked マスタ同期 to
    // carry 保険者 in it too, which is what forced the rename:
    //
    //   マスタ同期            コード / 名称       — whichever master the run is about (治療院 OR 保険者)
    //   レセプト同期・審査通知  治療院ID / 治療院名  — always a clinic
    //
    // Headed 治療院ID on a row about a 保険者 would be simply wrong, and 対象 was tried before and says
    // nothing on screen. データ種別 sits immediately left and already names WHICH kind, so 「コード」 and
    // 「名称」 read as 「the code of that」 without a longer header.
    // Both are dropped outright on the 治療院 portal — see showClinicColumns.
    ...(showClinicColumns ? [
    {
      title: isMaster ? 'コード' : '治療院ID',
      key: 'code',
      render: (_: unknown, record: NotificationItem) => (
        <span className="font-mono text-xs whitespace-nowrap">
          {(isMaster ? record.targetCode : record.clinicNumber) || ''}
        </span>
      ),
      width: 1,
    },
    {
      // The name IS BACK, reversing the earlier code-only decision. The reason it was dropped still stands
      // — it is the widest thing on the table — so it is TRUNCATED rather than allowed to set the table's
      // width: max 14rem, ellipsis, full name in the tooltip.
      //
      // Blank, not 「-」, when there is none: a dash reads as missing data, while a run that names no
      // single record genuinely has nothing to put here.
      title: isMaster ? '名称' : '治療院名',
      key: 'name',
      render: (_: unknown, record: NotificationItem) => {
        const value = isMaster ? record.targetName : record.clinicName;
        return value ? (
          <AppTooltip title={value} showOnlyOnOverflow>
            <span className="block max-w-[14rem] truncate">{value}</span>
          </AppTooltip>
        ) : (
          <span />
        );
      },
      width: 1,
    },
    ] : []),

    // 受診者番号 + 受診者名 — 審査システムからの通知 only. Same order and same headers as
    // 返戻・問合せレセプト登録, which is where this row is acted on.
    //
    // THE NUMBER IS SCOPED PER CLINIC and restarts at 1 for every one of them, so 「1042」 alone names
    // nobody. On the admin portal that is why it is never drawn apart from 治療院ID; on the 治療院 one the
    // clinic is the whole screen, so the number is unambiguous without a column repeating it.
    ...(isInbound
      ? [
          {
            title: PATIENT_COLUMN.SERIAL,
            dataIndex: 'patientSerial',
            key: 'patientSerial',
            render: (value: string | null) => (
              <span className="font-mono text-xs whitespace-nowrap">{value || '-'}</span>
            ),
            width: 1,
            align: 'right' as const,
          },
          {
            title: PATIENT_COLUMN.NAME,
            dataIndex: 'patientName',
            key: 'patientName',
            // nowrap for the same reason as every other short CJK cell here: 「width: 1」 hugs min-content,
            // and min-content for a 氏名 is ONE character.
            render: (value: string | null) => <span className="whitespace-nowrap">{value || '-'}</span>,
            width: 1,
          },
        ]
      : []),

    // 提出年月 — the two tabs whose rows belong to a submission period.
    ...(isMaster
      ? []
      : [
          {
            title: '提出年月',
            dataIndex: 'submitYearMonth',
            key: 'submitYearMonth',
            render: (value: string | null) => (
              <span className="whitespace-nowrap tabular-nums">{yearMonth(value)}</span>
            ),
            width: 1,
          },
        ]),

    // 成功件数 / 失敗件数 ARE NO LONGER COLUMNS. 👤 Owner's manager, 2026-08-27: the counts belong in the
    // 内容 sentence (「120件中 118件成功、2件エラー」), where they read as one statement instead of two
    // numbers a reader has to pair up — and where they appear on every tab, not only レセプト同期.

    {
      title: '内容',
      key: 'summary',
      render: (_, record) => <Summary item={record} showAdminContactHint={showAdminContactHint} />,
      // 100% — this column swallows whatever the fixed ones leave over, so the slack lands on the sentence
      // rather than on the button column.
      width: '100%',
    },
    {
      title: '既読',
      key: 'read',
      // whitespace-nowrap IS LOAD-BEARING, not decoration.
      //
      // The 「width: 1」 idiom makes a column shrink to its MIN-CONTENT, and min-content for Japanese is ONE
      // CHARACTER — CJK wraps between any two glyphs, with no space needed. So 「既読」 collapsed to a
      // two-line column barely wider than a single 「既」. nowrap raises min-content to the whole word,
      // which is what the idiom then hugs. Every other short CJK cell on this table needs the same guard.
      render: (_, record) =>
        record.read ? (
          <span className="whitespace-nowrap text-gray-400">既読</span>
        ) : (
          <span className="whitespace-nowrap font-semibold text-blue-600">未読</span>
        ),
      width: 1,
      align: 'center',
    },
    // エラー詳細 (CSV) and 操作 (再送信) — the two SENDING tabs only, for the same reason as 結果 above:
    // nothing on the inbound tab can fail, so there is no error file to download and nothing to re-push.
    ...(isInbound
      ? []
      : [
          {
            title: 'エラー詳細',
            key: 'errorDetail',
            render: (_: unknown, record: NotificationItem) =>
              // Drift first: such a row can never satisfy hasErrorDetail (it has no sends), but stating the
              // order here means a future row that somehow had both would still offer the file that
              // matches what it IS rather than the one that matches what it counted.
              record.hasDriftCsv && onDownloadDrift ? (
                <AppTooltip title="状態が一致しないレセプトの明細をCSVでダウンロードします">
                  <AppButton
                    size="small"
                    icon={<Download />}
                    disabled={disabled}
                    onClick={() => onDownloadDrift(record)}
                  >
                    CSV
                  </AppButton>
                </AppTooltip>
              ) : hasErrorDetail(record) ? (
                <AppTooltip title="エラーの詳細をCSVでダウンロードします">
                  <AppButton
                    size="small"
                    icon={<Download />}
                    disabled={disabled}
                    onClick={() => onDownloadError(record)}
                  >
                    CSV
                  </AppButton>
                </AppTooltip>
              ) : (
                // 「-」 rather than a disabled button: there is nothing to download on a success row, and a
                // greyed button invites a press that can never do anything.
                <span className="text-gray-300">-</span>
              ),
            width: 1,
            align: 'center' as const,
          },
          // 操作 IS NOT A COLUMN — 👤 owner, 2026-08-27: 「đã có checkbox」.
          //
          // The per-row 再送信 button duplicated the bulk one exactly, on a screen where ticking one row is
          // already the shortest path to it. Worse, the two had DIFFERENT RULES after the all-or-nothing
          // change: the bulk button refuses a mixed selection, while a row button could still fire on its
          // own — two ways to do one thing, disagreeing about when it is allowed.
          //
          // エラー詳細 STAYS a column: a CSV download is per-row by nature and has no bulk counterpart.
        ]),
  ];

  return (
    <div className="app-noti-table">
      <AppTable
        columns={columns}
        dataSource={data}
        rowKey={record => record.id}
        tableLayout="auto"
        scroll={{ x: true }}
        loading={isLoading}
        hasFilters={hasFilters}
        // 種別 drives the left bar and the row tint; read state drives the weight of 内容.
        //
        // NO LEVEL CLASS ON THE INBOUND TAB. Every row there is 情報, so a bar down all twenty rows carries
        // no information and only competes with the 種別 tag, which is the column that does.
        rowClassName={record =>
          [
            'app-noti-row',
            isInbound ? '' : `app-noti-row--${record.level}`,
            record.read ? 'app-noti-row--read' : 'app-noti-row--unread',
          ]
            .filter(Boolean)
            .join(' ')
        }
        rowSelection={{
          selectedRowKeys: selectedIds,
          onChange: keys => onSelectionChange(keys as number[]),
          // 全選択 covers THIS PAGE only, which is what the header checkbox can honestly promise — the
          // screen cannot name the rows on pages it has not drawn, and the bulk actions take an id list.
          columnWidth: 48,
          // Explicit so the header offers 「このページを全選択」 and 「選択を解除」 and nothing that reaches
          // past the page.
          selections: false,
        }}
      />
    </div>
  );
};

export default NotificationCenterTable;
